import { isFalsy } from '@leapfinance/frontend-commons';

import { Shape } from '@/types/common';
import {
  VASCreateAccomodationRequestBodyType,
  VASRequestCallbackRequestBodyType,
  VASUpdateAccomodationRequestBodyType,
} from '@/types/vas/accomodation';
import { VASCreateFlywirePaymentLinkRequestBodyType } from '@/types/vas/flywire';
import {
  VASCreateGICRequestBodyType,
  VASUpdateGICRequestBodyType,
} from '@/types/vas/gic';
import {
  VASCreateLoansRequestBodyType,
  VASUpdateLoansRequestBodyType,
} from '@/types/vas/loan';
import { alphabetRegex } from '@/utils/regexExpressions';
import yup from '@/utils/yup/yupInstance';

const trimValues = (value: string) => {
  return value.trim();
};

const optionSchema = yup.object().shape({
  label: yup.string(),
  value: yup.string(),
});

function optionSchemaTest(this: any, item: any) {
  const isItemNotSelected = !item?.label || !item?.value;
  if (isItemNotSelected) {
    return this.createError({
      path: ``,
      message: `Required`,
    });
  }

  return true;
}

function contactSchemaTest(this: any, item: any) {
  if (
    (!isFalsy(this.parent?.countryCode?.value) &&
      this.parent?.countryCode?.value !== `+91`) ||
    // TODO:: this for request tracker forms - need a better solutoin
    (!isFalsy(this.parent?.countrycode?.value) &&
      this.parent?.countrycode?.value !== `+91`)
  ) {
    if (isFalsy(item)) {
      return this.createError({
        path: ``,
        message: `Contact Number is required`,
      });
    }
    return true;
  }

  if (isFalsy(item)) {
    return this.createError({
      path: ``,
      message: `Contact Number is required`,
    });
  }

  if ((item || ``).charAt(0) === `0`) {
    return this.createError({
      path: ``,
      message: `Contact number cannot start with 0`,
    });
  }

  if ((item || ``).length !== 10 || !/^\d+$/.test(item || ``)) {
    return this.createError({
      path: ``,
      message: `Contact number must be exactly 10 digits`,
    });
  }
  return true;
}

const contactSchema = yup
  .string()
  .test(
    `valid-contact-number`,
    `Invalid phone number`,
    contactSchemaTest as any,
  );

const emailSchema = yup
  .string()
  .email(`Invalid email format`)
  .max(255, `Email must be at most 255 characters`);

const studentNameSchema = yup
  .string()
  .max(50, `Max 50 characters only`)
  .transform(trimValues);

const CountryTextFieldSchema = yup
  .string()
  .matches(
    /^[a-zA-Z ]*$/,
    `Destination country must contain only alphabetical characters and spaces`,
  )
  .max(56, `Destination country must be at most 56 characters`);

const CityTextFieldSchema = yup
  .string()
  .matches(
    /^[a-zA-Z]+(?: [a-zA-Z]+)*$/,
    `Preferred city must contain only alphabetical characters and spaces`,
  )
  .max(85, `Preferred city must be at most 85 characters`);

const RequestCallbackContactNumberSchema = yup
  .object()
  .shape<Shape<VASRequestCallbackRequestBodyType>>({
    callBackContactNumber: yup
      .mixed()
      .required(`Mobile Number is required`)
      .test(
        `checkUniqueMobileNumber`,
        `This email is already registered.`,
        function (item, context) {
          const mobileNumber = (item as any)?.number;
          const countryCode = (item as any)?.countryCode;

          if (!isFalsy(countryCode) && countryCode !== `+91`) {
            if (isFalsy(mobileNumber)) {
              return this.createError({
                path: ``,
                message: `Contact Number is required`,
              });
            }
            return true;
          }

          if (isFalsy(mobileNumber)) {
            return this.createError({
              path: ``,
              message: `Contact Number is required`,
            });
          }

          if ((mobileNumber || ``).charAt(0) === `0`) {
            return this.createError({
              path: ``,
              message: `Contact number cannot start with 0`,
            });
          }

          if (
            (mobileNumber || ``).length !== 10 ||
            !/^\d+$/.test(mobileNumber || ``)
          ) {
            return this.createError({
              path: ``,
              message: `Contact number must be exactly 10 digits`,
            });
          }

          return true;
        },
      ),
    serviceCategory: yup.string().optional(),
    // countryCode: optionSchema
    //   .nullable()
    //   .test(`has-label-value`, ``, optionSchemaTest),
  });

const StudentCreationFormSchema = yup.object().shape({
  firstName: studentNameSchema.required(`Required`),
  lastName: studentNameSchema.required(`Required`),
});

export type VasStudentCreationRequestBodyType = {
  createStudentMode: 'yes' | 'no';
  firstName: string;
  lastName: string;
  student: {
    label: string;
    value: string | number;
  };
};

const VASStudentCreationSchema = yup
  .object()
  .shape<Shape<VasStudentCreationRequestBodyType>>({
    createStudentMode: yup.string().oneOf([`yes`, `no`]).required(`Required`),
    firstName: yup.string().when(`createStudentMode`, {
      is: (createStudentMode: 'yes' | 'no') => createStudentMode === `yes`,
      then: studentNameSchema
        .required(`Required`)
        .matches(alphabetRegex, { message: `Only alphabets are allowed` }),
      otherwise: studentNameSchema.optional().nullable(),
    }),
    lastName: yup.string().when(`createStudentMode`, {
      is: (createStudentMode: 'yes' | 'no') => createStudentMode === `yes`,
      then: studentNameSchema
        .required(`Required`)
        .matches(alphabetRegex, { message: `Only alphabets are allowed` }),
      otherwise: studentNameSchema.optional().nullable(),
    }),
    student: optionSchema.when(`createStudentMode`, {
      is: (createStudentMode: 'yes' | 'no') => createStudentMode === `yes`,
      then: optionSchema.nullable().optional(),
      otherwise: optionSchema
        .nullable()
        .test(`has-label-value`, ``, optionSchemaTest),
    }),
  });

const AccomodationFormSchema = yup.object().shape<
  Shape<
    VASCreateAccomodationRequestBodyType & {
      intake: {
        label: string;
        value: string | number;
      };
    }
  >
>({
  contactNumber: contactSchema.nullable(),
  emailId: emailSchema.nullable().required(`Email is Required`),
  countryCode: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  universityName: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  destinationCountry: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  destinationCity: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  intake: optionSchema.nullable().test(`has-label-value`, ``, optionSchemaTest),
  recipientEmailIds: yup.array(yup.string()),
});

const LoansFormSchema = yup.object().shape<
  Shape<
    VASCreateLoansRequestBodyType & {
      permissionRequired: any;
    }
  >
>({
  contactNumber: contactSchema.nullable(),
  emailId: emailSchema.nullable().required(`Email is Required`),
  countryCode: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  universityName: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  destinationCountry: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  type: optionSchema.nullable(),
  permissionRequired: yup.array().of(optionSchema).min(1, `Required`),
});

const LoanRequestTrackerFormSchema = yup
  .object()
  .shape<Shape<VASUpdateLoansRequestBodyType>>({
    contactNumber: contactSchema.nullable(),
    emailId: emailSchema.nullable().required(`Email is Required`),
    countrycode: optionSchema
      .nullable()
      .test(`has-label-value`, ``, optionSchemaTest),
    universityName: optionSchema
      .nullable()
      .test(`has-label-value`, ``, optionSchemaTest),
    destinationCountry: optionSchema
      .nullable()
      .test(`has-label-value`, ``, optionSchemaTest),
    type: optionSchema.nullable().test(`has-label-value`, ``, optionSchemaTest),
  });

const AccomodationRequestTrackerFormSchema = yup.object().shape<
  Shape<
    VASUpdateAccomodationRequestBodyType & {
      intake: {
        label: string;
        value: string | number;
      };
    }
  >
>({
  contactNumber: contactSchema.nullable(),
  emailId: emailSchema.nullable().required(`Email is Required`),
  countrycode: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  universityName: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  destinationCountry: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  destinationCity: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  intake: optionSchema.nullable().test(`has-label-value`, ``, optionSchemaTest),
});

const GICFormSchema = yup.object().shape<Shape<VASCreateGICRequestBodyType>>({
  countryCode: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  contactNumber: contactSchema.nullable(),
  emailId: emailSchema.nullable().required(`Email is Required`),
  intake: optionSchema.nullable().test(`has-label-value`, ``, optionSchemaTest),
  universityName: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  bankName: yup.string().required(`Required`),
  recipientEmailIds: yup.array(yup.string()),
});

const GICRequestTrackerFormSchema = yup.object().shape<
  Shape<
    VASUpdateGICRequestBodyType & {
      intake: {
        label: string;
        value: string;
      };
    }
  >
>({
  countrycode: optionSchema.test(`has-label-value`, ``, optionSchemaTest),
  contactNumber: contactSchema.nullable(),
  emailId: emailSchema.required(`Email is Required`),
  universityName: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  intake: optionSchema.nullable().test(`has-label-value`, ``, optionSchemaTest),
});

const FlywireFormSchema = yup.object().shape<
  Shape<
    VASCreateFlywirePaymentLinkRequestBodyType &
      VasStudentCreationRequestBodyType & {
        applicationExists: boolean;
        permissionRequired: any;
      }
  >
>({
  ...VASStudentCreationSchema.fields,
  emailId: emailSchema.nullable().required(`Email is Required`),
  countryCode: optionSchema
    .nullable()
    .test(`has-label-value`, ``, optionSchemaTest),
  contactNumber: contactSchema.nullable(),
  applicationId: yup.string().when(`student`, {
    is: (student: any) => student?.value !== undefined,
    then: yup.string().when(`applicationExists`, {
      is: (exists: any) => {
        return exists === true;
      },
      then: yup.string().required(`Required`),
      otherwise: yup.string().nullable().optional(),
    }),
    otherwise: yup.string().nullable().optional(),
  }),
  universityName: optionSchema.when(`createStudentMode`, {
    is: (createStudentMode: 'yes' | 'no') => createStudentMode === `yes`,
    then: optionSchema.nullable().optional(),
    otherwise: optionSchema
      .nullable()
      .test(`has-label-value`, ``, optionSchemaTest),
  }),
  permissionRequired: yup
    .array()
    .of(optionSchema)
    .when(`createStudentMode`, {
      is: (createStudentMode: 'yes' | 'no') => createStudentMode === `yes`,
      then: yup.array().of(optionSchema).optional(),
      otherwise: yup.array().of(optionSchema).min(1, `Required`),
    }),
});

export {
  contactSchema,
  RequestCallbackContactNumberSchema,
  emailSchema,
  studentNameSchema,
  AccomodationFormSchema,
  optionSchema,
  optionSchemaTest,
  AccomodationRequestTrackerFormSchema,
  StudentCreationFormSchema,
  GICRequestTrackerFormSchema,
  GICFormSchema,
  VASStudentCreationSchema,
  FlywireFormSchema,
  LoanRequestTrackerFormSchema,
  LoansFormSchema,
};
